'use client';

import { useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import clsx from 'clsx';
import { Button, Form, Input, Label, P, Select, Textarea, SURFACE_STATIC, cn } from '@/components/ui';
import { SITE_CONTACT_EMAIL, SITE_CONTACT_MAILTO } from '@/constants/siteContact';
import { INQUIRY_TYPE_IDS, type InquiryTypeId } from '@/lib/contact/inquiryTypes';
import { submitInquiry } from '@/lib/contact/submitInquiryClient';
import { INQUIRY_HONEYPOT_FIELD } from '@/lib/contact/validateInquiry';

export type SiteInquiryFormProps = {
  defaultType?: InquiryTypeId;
  showTypeSelect?: boolean;
  className?: string;
  labels?: Partial<{
    submit: string;
    name: string;
    email: string;
    company: string;
    message: string;
  }>;
};

export default function SiteInquiryForm({
  defaultType = 'general',
  showTypeSelect = false,
  className,
  labels,
}: SiteInquiryFormProps) {
  const t = useTranslations('inquiry');
  const locale = useLocale();
  const pathname = usePathname();
  const [type, setType] = useState<InquiryTypeId>(defaultType);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const source = pathname || undefined;

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === 'sending') return;

    const form = e.currentTarget;
    const fd = new FormData(form);

    setStatus('sending');
    setErrorMessage(null);

    const email = String(fd.get('email') ?? '').trim();
    const trapRaw = String(fd.get(INQUIRY_HONEYPOT_FIELD) ?? '').trim();
    // Browsers/password managers sometimes autofill hidden traps with email — treat as empty.
    const honeypot =
      trapRaw === email || trapRaw === String(fd.get('name') ?? '').trim() ? '' : trapRaw;

    const result = await submitInquiry({
      type: (showTypeSelect ? String(fd.get('type')) : defaultType) as InquiryTypeId,
      name: String(fd.get('name') ?? ''),
      email,
      company: String(fd.get('company') ?? '') || undefined,
      phone: String(fd.get('phone') ?? '') || undefined,
      message: String(fd.get('message') ?? ''),
      source,
      locale,
      [INQUIRY_HONEYPOT_FIELD]: honeypot,
    });

    if (result.ok) {
      setStatus('success');
      form.reset();
      setType(defaultType);
      return;
    }

    setStatus('error');
    setErrorMessage(result.error);
  }

  if (status === 'success') {
    return (
      <div className={cn('page-glass-panel page-glass-panel--elevated p-5 sm:p-6', SURFACE_STATIC, className)}>
        <P className="m-0 text-sm font-medium text-prose">{t('success')}</P>
        <P className="m-0 mt-2 text-sm text-prose-muted">
          <a href={SITE_CONTACT_MAILTO} className="font-medium text-[var(--color-primary)] hover:underline">
            {SITE_CONTACT_EMAIL}
          </a>
        </P>
      </div>
    );
  }

  return (
    <Form className={clsx('form page-inquiry-form', className)} onSubmit={handleSubmit}>
      <input
        type="text"
        name={INQUIRY_HONEYPOT_FIELD}
        tabIndex={-1}
        autoComplete="off"
        defaultValue=""
        readOnly
        data-lpignore="true"
        data-1p-ignore="true"
        data-form-type="other"
        className="pointer-events-none absolute -left-[9999px] h-px w-px opacity-0"
        aria-hidden
      />

      {showTypeSelect ? (
        <div className="page-inquiry-field">
          <Label htmlFor="inq-type" className="label">
            {t('typeLabel')}
          </Label>
          <Select
            id="inq-type"
            name="type"
            className="select"
            value={type}
            onChange={(e) => setType(e.target.value as InquiryTypeId)}
            required
          >
            {INQUIRY_TYPE_IDS.map((id) => (
              <option key={id} value={id} className="option">
                {t(`types.${id}`)}
              </option>
            ))}
          </Select>
        </div>
      ) : (
        <input type="hidden" name="type" defaultValue={defaultType} />
      )}

      <div className="page-inquiry-grid sm:grid-cols-2">
        <div className="page-inquiry-field">
          <Label htmlFor="inq-name" className="label">
            {labels?.name ?? t('nameLabel')}
          </Label>
          <Input id="inq-name" name="name" className="input" required autoComplete="name" />
        </div>
        <div className="page-inquiry-field">
          <Label htmlFor="inq-email" className="label">
            {labels?.email ?? t('emailLabel')}
          </Label>
          <Input id="inq-email" name="email" type="email" className="input" required autoComplete="email" />
        </div>
      </div>

      <div className="page-inquiry-grid sm:grid-cols-2">
        <div className="page-inquiry-field">
          <Label htmlFor="inq-company" className="label">
            {labels?.company ?? t('companyLabel')}
          </Label>
          <Input id="inq-company" name="company" className="input" autoComplete="organization" />
        </div>
        <div className="page-inquiry-field">
          <Label htmlFor="inq-phone" className="label">
            {t('phoneLabel')}
          </Label>
          <Input id="inq-phone" name="phone" type="tel" className="input" autoComplete="tel" />
        </div>
      </div>

      <div className="page-inquiry-field">
        <Label htmlFor="inq-message" className="label">
          {labels?.message ?? t('messageLabel')}
        </Label>
        <Textarea id="inq-message" name="message" className="textarea" rows={5} required minLength={10} />
        <P className="m-0 text-xs text-prose-muted">{t('messageHint')}</P>
      </div>

      {status === 'error' ? (
        <P className="m-0 text-sm text-red-600 dark:text-red-400" role="alert">
          {errorMessage ?? t('errorGeneric')}
        </P>
      ) : null}

      <Button type="submit" variant="primary" className="w-full sm:w-auto" disabled={status === 'sending'}>
        {status === 'sending' ? t('sending') : (labels?.submit ?? t('submit'))}
      </Button>
    </Form>
  );
}
